import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, HeartHandshake, Users, LogIn, UserPlus, ArrowRight, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const inputCls = "w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-colors";

const SpaceForm = ({ space, icon: Icon, subtitle, onClose, allowRegister }) => {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [name, setName] = useState("");
  const [identifier, setId] = useState("");
  const [password, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [session, setSession] = useState(null);
  const { refreshPortal } = useApp();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      let data;
      if (mode === "register") {
        ({ data } = await api.post("/portal/register", { name, email: identifier, password }));
      } else {
        ({ data } = await api.post("/portal/login", { space, identifier, password }));
      }
      if (refreshPortal) await refreshPortal();
      if (space === "soignant") { onClose && onClose(); nav("/espace-soignant"); return; }
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Une erreur est survenue");
    }
    setLoading(false);
  };

  if (session) {
    return (
      <div className="text-center py-6" data-testid={`portal-success-${space}`}>
        <div className="w-14 h-14 rounded-full bg-secondary/30 flex items-center justify-center mx-auto"><Check className="w-7 h-7 text-secondary-foreground" /></div>
        <h3 className="font-serif-display text-2xl mt-5">Bienvenue, {session.name}</h3>
        <p className="text-foreground/70 mt-2 text-sm">Vous êtes connecté(e). Vous pouvez maintenant commander et échanger sur le forum.</p>
        <div className="flex flex-col gap-3 mt-6">
          {session.app_url && (
            <a href={session.app_url} target="_blank" rel="noopener noreferrer" data-testid="portal-open-app"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3 hover:scale-105 transition-transform duration-300">
              Ouvrir l'application Passerelle <ArrowRight className="w-4 h-4" />
            </a>
          )}
          <button onClick={() => onClose && onClose()} className="text-sm text-muted-foreground hover:text-primary" data-testid="portal-close-btn">Continuer sur le site</button>
        </div>
      </div>
    );
  }

  const isRegister = mode === "register";
  return (
    <form onSubmit={submit} className="space-y-4 pt-2" data-testid={`portal-form-${space}`}>
      <div className="flex items-center gap-3 text-foreground/70">
        <span className="w-10 h-10 rounded-full bg-accent flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></span>
        <p className="text-sm">{isRegister ? "Créez votre compte famille pour commander et échanger." : subtitle}</p>
      </div>
      {isRegister && (
        <label className="block">
          <span className="text-sm text-foreground/70 mb-1.5 block">Votre nom</span>
          <input required className={inputCls} data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
      )}
      <label className="block">
        <span className="text-sm text-foreground/70 mb-1.5 block">Adresse email</span>
        <input type="email" required className={inputCls} data-testid={`portal-id-${space}`} value={identifier} onChange={(e) => setId(e.target.value)} />
      </label>
      <label className="block">
        <span className="text-sm text-foreground/70 mb-1.5 block">Mot de passe {isRegister && <span className="text-muted-foreground">(min. 6 caractères)</span>}</span>
        <input type="password" required className={inputCls} data-testid={`portal-pwd-${space}`} value={password} onChange={(e) => setPwd(e.target.value)} />
      </label>
      {error && <p className="text-destructive text-sm" data-testid={`portal-error-${space}`}>{error}</p>}
      <button disabled={loading} data-testid={isRegister ? "register-submit" : `portal-submit-${space}`} className="w-full rounded-full bg-primary text-primary-foreground py-3.5 flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (isRegister ? <UserPlus className="w-4 h-4" /> : <LogIn className="w-4 h-4" />)}
        {isRegister ? "Créer mon compte" : "Se connecter"}
      </button>
      {allowRegister && (
        <p className="text-center text-sm text-muted-foreground">
          {isRegister ? "Vous avez déjà un compte ? " : "Pas encore de compte ? "}
          <button type="button" onClick={() => { setMode(isRegister ? "login" : "register"); setError(""); }} data-testid="toggle-register" className="text-primary font-medium hover:underline">
            {isRegister ? "Se connecter" : "Créer un compte"}
          </button>
        </p>
      )}
    </form>
  );
};

export const ConnexionDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md z-[80]" data-testid="connexion-dialog">
      <DialogHeader>
        <DialogTitle className="font-serif-display text-3xl font-light">Connexion</DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">Choisissez votre espace pour vous connecter ou créer un compte.</DialogDescription>
      </DialogHeader>
      <Tabs defaultValue="famille" className="mt-2">
        <TabsList className="grid grid-cols-2 bg-muted/60 rounded-full p-1">
          <TabsTrigger value="famille" data-testid="portal-tab-famille" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Espace Famille</TabsTrigger>
          <TabsTrigger value="soignant" data-testid="portal-tab-soignant" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Espace Soignant</TabsTrigger>
        </TabsList>
        <TabsContent value="famille" className="mt-5">
          <SpaceForm space="famille" icon={Users} onClose={() => onOpenChange(false)} allowRegister
            subtitle="Retrouvez vos accès, commandez et rejoignez le forum." />
        </TabsContent>
        <TabsContent value="soignant" className="mt-5">
          <SpaceForm space="soignant" icon={HeartHandshake} onClose={() => onOpenChange(false)}
            subtitle="Accompagnez vos patients avec les ressources Passerelle." />
        </TabsContent>
      </Tabs>
    </DialogContent>
  </Dialog>
);
